import type { ProfileId } from "@pax/api-contract/common";
import { infiniteQueryOptions, type QueryClient } from "@tanstack/react-query";
import { api, orpc, publicApi, publicOrpc, reachable } from "@/lib/api";

/** How many published presets a profile page shows. */
const PROFILE_PRESET_COUNT = 24;

/** How many friends a profile's friends list loads at a time. */
const PROFILE_FRIEND_COUNT = 100;

/** How many assets and collections a profile's tabs show. */
const PROFILE_SHELF_COUNT = 30;

export const profileQuery = (profileId: string) =>
  orpc.profiles.get.queryOptions({
    input: { params: { profileId } },
    select: response => response.profile,
  });

/** The profile currently holding a username. The response is the same as `profileQuery`'s. */
export const profileByUsernameQuery = (username: string) =>
  orpc.profiles.byUsername.get.queryOptions({ input: { params: { username } } });

export const profileStatsQuery = (profileId: string) =>
  orpc.profiles.stats.get.queryOptions({
    input: { params: { profileId } },
    staleTime: 5 * 60_000,
    retry: false,
  });

/** Someone's published presets, a page at a time. */
export const profilePresetsQuery = (profileId: string) =>
  orpc.presets.list.infiniteOptions({
    input: (cursor: string | undefined) => ({
      query: {
        ownerId: profileId as ProfileId,
        limit: PROFILE_PRESET_COUNT,
        ...(cursor ? { cursor } : {}),
      },
    }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: response => response.nextCursor ?? undefined,
    staleTime: 60_000,
  });

/** Your friends, a page at a time. */
export const friendPagesQuery = orpc.friends.list.infiniteOptions({
  input: (cursor: string | undefined) => ({
    query: { tab: "friends", limit: PROFILE_FRIEND_COUNT, ...(cursor ? { cursor } : {}) },
  }),
  initialPageParam: undefined,
  getNextPageParam: response => response.nextCursor ?? undefined,
});

export const relationshipQuery = (profileId: string) =>
  orpc.friends.relationships.get.queryOptions({
    input: { params: { profileId: profileId as ProfileId } },
  });

export const mutualFriendsQuery = (profileId: string) =>
  orpc.friends.mutualFriends.list.queryOptions({
    input: {
      params: { profileId: profileId as ProfileId },
      query: { limit: PROFILE_FRIEND_COUNT },
    },
  });

/** The friends you share with someone, a page at a time. */
export const mutualFriendPagesQuery = (profileId: string) =>
  orpc.friends.mutualFriends.list.infiniteOptions({
    input: (cursor: string | undefined) => ({
      params: { profileId: profileId as ProfileId },
      query: { limit: PROFILE_FRIEND_COUNT, ...(cursor ? { cursor: cursor as ProfileId } : {}) },
    }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: response => response.nextCursor ?? undefined,
  });

/** Live checks of a username while it's typed: its format, whether it's free, and renames left. */
export const usernameAvailabilityQuery = (username: string) =>
  orpc.profiles.usernameAvailability.get.queryOptions({
    input: { query: { username } },
    staleTime: 10_000,
  });

/**
 * Someone's published assets, most recently edited first, a page at a time. `own` asks only for
 * published ones, since asking about yourself would otherwise include your drafts.
 */
export const profileAssetsQuery = (profileId: string, own: boolean) =>
  infiniteQueryOptions({
    queryKey: [...publicOrpc.assets.list.key(), { ownerId: profileId, own }],
    queryFn: ({ pageParam }) =>
      reachable(
        publicApi.assets.list({
          query: {
            ownerId: profileId as ProfileId,
            status: own ? "published" : undefined,
            limit: PROFILE_SHELF_COUNT,
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: response => response.nextCursor ?? undefined,
    staleTime: 60_000,
  });

/** The collections someone owns or collaborates on, most recently changed first, a page at a time. */
export const profileCollectionsQuery = (profileId: string) =>
  infiniteQueryOptions({
    queryKey: [...publicOrpc.assets.collections.list.key(), { editorId: profileId }],
    queryFn: ({ pageParam }) =>
      reachable(
        publicApi.assets.collections.list({
          query: {
            editorId: profileId as ProfileId,
            sort: "recentlyChanged",
            limit: PROFILE_SHELF_COUNT,
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: response => response.nextCursor ?? undefined,
    staleTime: 60_000,
  });

/** The four ways a friend button changes where you stand with someone. */
export type FriendChange = "request" | "cancel" | "accept" | "remove";

export async function changeFriendship(
  queryClient: QueryClient,
  profileId: string,
  change: FriendChange,
): Promise<void> {
  const id = profileId as ProfileId;
  if (change === "request")
    await reachable(api.friends.requests.create({ body: { profileId: id } }));
  if (change === "cancel")
    await reachable(api.friends.requests.delete({ params: { profileId: id } }));
  if (change === "accept")
    await reachable(api.friends.requests.accept({ params: { profileId: id } }));
  if (change === "remove") await reachable(api.friends.delete({ params: { profileId: id } }));
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: orpc.friends.key() }),
    queryClient.invalidateQueries({ queryKey: orpc.profiles.get.key() }),
  ]);
}
